import { appRoutes } from "@/app/router";
import { isEditableElement } from "@/modules/html";
import { CleanableNote } from "@/modules/keyboard-shortcuts/types";
import { Tab } from "@/store/navigation/types";
import { matchPath } from "react-router";

export const MAIN_PANEL_SPLIT_VIEW_PANEL_GROUP_ID = "main-panel-split-view-panel-group";

export const isCleanableNote = (note: unknown): note is CleanableNote =>
  typeof note === "object" &&
  note !== null &&
  "cleanup" in note &&
  typeof note.cleanup === "function";

export const getFocusedMemItEditableElement = (): HTMLElement | null => {
  const activeElement = document.activeElement;

  if (!(activeElement instanceof HTMLElement) || !isEditableElement(activeElement)) {
    return null;
  }

  return activeElement.closest('[data-component="MemItSidePanelView"]') ? activeElement : null;
};

export const focusPaneForKeyboardInteraction = (paneId: string) => {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return;
  }

  window.requestAnimationFrame(() => {
    const paneElement = document.getElementById(paneId);
    if (!(paneElement instanceof HTMLElement)) {
      return;
    }

    const editableElement = paneElement.querySelector<HTMLElement>('[contenteditable="true"]');
    if (editableElement) {
      editableElement.focus();
      return;
    }

    const previousTabIndex = paneElement.getAttribute("tabindex");
    if (!paneElement.hasAttribute("tabindex")) {
      paneElement.setAttribute("tabindex", "-1");
    }

    paneElement.focus();

    if (previousTabIndex === null) {
      paneElement.removeAttribute("tabindex");
    }
  });
};

export const isNotesListPath = (pathname: string) =>
  pathname === appRoutes.notesList({}).path ||
  !!matchPath(
    appRoutes.importBatchNotes({ params: { importBatchId: ":importBatchId" } }).path,
    pathname
  );

export const getMainPanelSplitViewContainerWidth = (): number | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }

  const element = document.getElementById(MAIN_PANEL_SPLIT_VIEW_PANEL_GROUP_ID);
  if (!element) {
    return undefined;
  }

  return element.getBoundingClientRect().width;
};

export const getVisiblePaneTabs = ({
  activeTabId,
  panesMap,
  tabsMap,
  getResponsivePanes,
  containerWidth,
}: {
  activeTabId?: string;
  panesMap?: {
    size: number;
    values(): IterableIterator<{ activeTabId: string | null }>;
  };
  tabsMap: {
    get(key: string): Tab | undefined;
  };
  getResponsivePanes?: (containerWidth: number) => {
    visiblePanes: Array<{ activeTabId: string | null }>;
  };
  containerWidth?: number;
}) => {
  if (typeof containerWidth === "number" && containerWidth > 0 && getResponsivePanes) {
    return getResponsivePanes(containerWidth)
      .visiblePanes.flatMap(pane => (pane.activeTabId ? [tabsMap.get(pane.activeTabId)] : []))
      .filter((tab): tab is Tab => !!tab);
  }

  if (panesMap && panesMap.size > 0) {
    return Array.from(panesMap.values())
      .flatMap(pane => (pane.activeTabId ? [tabsMap.get(pane.activeTabId)] : []))
      .filter((tab): tab is Tab => !!tab);
  }

  const activeTab = activeTabId ? tabsMap.get(activeTabId) : undefined;
  return activeTab ? [activeTab] : [];
};
